import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, ShoppingBag, Mail, FileText } from "lucide-react";
import { api } from "../../services/api";

const DEFAULT_ITEMS = [
    {
        title: "Cameo — Svetelná technika",
        description:
            "Scénické LED svetlá, wash, beam, spot a stroboskopy pre profesionálne pódiá. Objednajte originálne produkty Cameo cez naše zastúpenie za distribučné ceny.",
        url: "https://www.adamhall.com/shop/en/brands/cameo",
    },
    {
        title: "Flight Case Materiál",
        description:
            "Originálny Adam Hall materiál — hliníkové profily, panty, rohové kódy, kolieska, uzávery a všetok sortiment na výrobu a opravu flight cases presne na mieru.",
        url: "https://www.adamhall.com/shop/en/flight-case-material",
    },
    {
        title: "Gravity — Stojany & príslušenstvo",
        description:
            "Prémiové mikrofónové a reproduktorové stojany, ramená, uchytenia a príslušenstvo pre profesionálnych umelcov aj fixné inštalácie.",
        url: "https://www.adamhall.com/shop/en/brands/gravity",
    },
];

const EMAIL = "info@kbmusic.sk";

export default function AdamHallShop() {
    const [items, setItems] = useState(DEFAULT_ITEMS);

    useEffect(() => {
        api.getSettings()
            .then((d) => {
                if (d.adam_hall_items && d.adam_hall_items.length > 0) {
                    setItems(d.adam_hall_items);
                }
            })
            .catch(() => {});
    }, []);

    if (items.length === 0) return null;

    return (
        <section
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="adamhall-section"
        >
            <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-nova-purple/8 rounded-full blur-[160px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-14 max-w-2xl"
                >
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-5">
                        / Obchod — Adam Hall distribútor
                    </div>

                    {/* Logo + heading inline */}
                    <div className="flex flex-wrap items-center gap-4 mb-5">
                        <img
                            src="/adam-hall-logo.svg"
                            alt="Adam Hall"
                            className="h-9 sm:h-11 w-auto opacity-95 shrink-0"
                        />
                        <div className="w-px h-8 bg-white/15 shrink-0 hidden sm:block" />
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-[1.08]">
                            Nakúpte profesionálne{" "}
                            <span className="text-nova-cyan">vybavenie</span>{" "}
                            priamo cez nás
                        </h2>
                    </div>

                    <p className="text-zinc-400 text-lg leading-relaxed">
                        KB MUSIC je autorizovaným partnerom{" "}
                        <span className="text-white font-medium">Adam Hall</span>.
                        Sprostredkujeme vám nákup profesionálneho vybavenia za
                        distribučné ceny — rýchlo, spoľahlivo a s podporou.
                    </p>
                </motion.div>

                {/* Product cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((item, i) => (
                        <motion.a
                            key={i}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="group relative flex flex-col border border-white/8 bg-nova-surface/40 rounded-2xl p-7 hover:border-nova-cyan/30 hover:bg-nova-surface/60 transition-all duration-300"
                        >
                            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-nova-cyan/5 to-transparent" />

                            <div className="relative flex items-start justify-between gap-3 mb-5">
                                <div className="w-10 h-10 rounded-xl bg-nova-cyan/10 border border-nova-cyan/20 flex items-center justify-center shrink-0 group-hover:border-nova-cyan/50 transition-colors">
                                    <ShoppingBag className="w-4 h-4 text-nova-cyan" />
                                </div>
                                <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-nova-cyan transition-colors mt-1 shrink-0" />
                            </div>

                            <h3 className="relative font-heading text-lg font-bold text-white group-hover:text-nova-cyan transition-colors mb-3 leading-tight">
                                {item.title}
                            </h3>
                            <p className="relative text-sm text-zinc-400 leading-relaxed flex-1">
                                {item.description}
                            </p>

                            <div className="relative mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-nova-cyan/60 group-hover:text-nova-cyan transition-colors">
                                <span>Zobraziť produkty</span>
                                <span className="group-hover:translate-x-1 transition-transform">→</span>
                            </div>
                        </motion.a>
                    ))}
                </div>

                {/* Email CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.35 }}
                    className="mt-10 rounded-2xl border border-white/10 bg-nova-surface/30 overflow-hidden"
                >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-6 sm:p-8">
                        <div className="flex-1">
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan mb-2">
                                / Cenová ponuka &amp; Objednávka
                            </div>
                            <p className="text-white text-base font-medium leading-snug mb-1">
                                Potrebujete väčšie množstvo alebo individuálnu cenu?
                            </p>
                            <p className="text-zinc-400 text-sm leading-relaxed">
                                Napíšte nám priamo na email — do 24 hodín dostanete
                                cenovú ponuku alebo potvrdenie objednávky.
                            </p>
                            <a
                                href={`mailto:${EMAIL}`}
                                className="inline-block mt-3 font-mono text-sm text-nova-cyan hover:underline tracking-wide"
                            >
                                {EMAIL}
                            </a>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto">
                            <a
                                href={`mailto:${EMAIL}?subject=Cenov%C3%A1%20ponuka%20%E2%80%94%20Adam%20Hall%20produkty`}
                                className="flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-nova-cyan/40 text-nova-cyan text-sm font-semibold hover:bg-nova-cyan/10 transition-colors"
                            >
                                <FileText className="w-4 h-4" />
                                Cenová ponuka
                            </a>
                            <a
                                href={`mailto:${EMAIL}?subject=Objedn%C3%A1vka%20%E2%80%94%20Adam%20Hall%20produkty`}
                                className="flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-nova-cyan text-nova-black text-sm font-semibold hover:bg-nova-cyan/90 transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                Objednávka emailom
                            </a>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
